import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Printer } from 'lucide-react';
import API_URL from '../../config/api';

// ⭐ Marca en el backend si el recibo de esta venta se imprimió o no
// (columna sales.receipt_printed), para que Reportes lo muestre por venta.
// Es "best effort": si falla (ej. venta ya cancelada, red caída), no debe
// bloquear el flujo de cobro — por eso el catch queda vacío.
function markReceiptPrinted(saleId, printed, token) {
  if (!saleId) return;
  axios.post(
    `${API_URL}/api/pos/sales/${saleId}/receipt-printed`,
    { printed },
    { headers: { Authorization: `Bearer ${token}` } }
  ).catch(() => {});
}

// ⭐ Impresión de recibo en térmica vía diálogo de impresión del navegador
// (window.print + CSS @media print), sin dependencias nuevas ni drivers
// ESC/POS. Funciona con cualquier térmica que ya tenga driver de Windows
// configurado como predeterminada (o se elige en el diálogo de impresión).
//
// Uso: se monta UNA vez por componente que puede cobrar (CajaVentas,
// MesasManager, PedidosPendientes) y se controla 100% con la prop
// `pendingReceipt`. Cuando pasa de null a un objeto, se muestra el
// confirm "¿Imprimir recibo?"; el objeto debe incluir un `onDone()` que
// el componente dueño ejecuta para hacer TODA la limpieza que antes
// pasaba inmediatamente al cobrar (limpiar carrito, cerrar modal, etc) —
// así el modal de cobros/carrito nunca se cierra antes de que el usuario
// decida si imprime o no.
const PAPER_WIDTHS = { '80': '80mm', '58': '58mm' };
const METHOD_LABELS = { cash: 'Efectivo', transfer: 'Transferencia', mixed: 'Mixto (Efectivo + Transferencia)' };

function getStoredWidth() {
  return localStorage.getItem('pos_receipt_width') === '58' ? '58' : '80';
}

function ReciboTicket({ sale, businessName, clientPhone, widthKey }) {
  const date = sale.created_at ? new Date(sale.created_at) : new Date();
  const fecha = date.toLocaleDateString('es-EC');
  const hora = date.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
  const items = Array.isArray(sale.items) ? sale.items : [];

  return (
    <div id="recibo-print-area">
      <style>{`
        #recibo-print-area { display: none; }
        @media print {
          @page { margin: 0; }
          body * { visibility: hidden !important; }
          #recibo-print-area, #recibo-print-area * { visibility: visible !important; }
          #recibo-print-area {
            display: block !important;
            position: absolute; top: 0; left: 0;
            width: ${PAPER_WIDTHS[widthKey] || '80mm'};
            font-family: 'Courier New', Courier, monospace;
            font-size: ${widthKey === '58' ? '10px' : '12px'};
            color: #000;
            padding: 4mm 3mm;
          }
          .recibo-line { border-top: 1px dashed #000; margin: 4px 0; }
          .recibo-row { display: flex; justify-content: space-between; gap: 8px; }
          .recibo-center { text-align: center; }
          .recibo-bold { font-weight: bold; }
        }
      `}</style>
      <div className="recibo-center recibo-bold">{businessName || 'Negocio'}</div>
      {clientPhone && <div className="recibo-center">Tel: {clientPhone}</div>}
      <div className="recibo-line" />
      <div>Fecha: {fecha} {hora}</div>
      <div>
        {sale.ticket_ref || `Ticket #${sale.id ?? ''}`}
        {sale.order_type === 'llevar' ? ' · Para llevar' : sale.order_type === 'servir' ? ' · Servir en mesa' : ''}
      </div>
      {sale.customer_name && <div>Cliente: {sale.customer_name}</div>}
      {sale.billing_type === 'factura' && sale.billing_customer && (
        <>
          <div className="recibo-line" />
          <div className="recibo-bold">DATOS DE FACTURACIÓN</div>
          <div>{sale.billing_customer.first_name} {sale.billing_customer.last_name}</div>
          {sale.billing_customer.cedula && <div>Cédula: {sale.billing_customer.cedula}</div>}
          {sale.billing_customer.phone && <div>Tel: {sale.billing_customer.phone}</div>}
          {sale.billing_customer.email && <div>{sale.billing_customer.email}</div>}
          {sale.billing_customer.sector && <div>{sale.billing_customer.sector}</div>}
        </>
      )}
      <div className="recibo-line" />
      {items.map((it, idx) => (
        <div key={idx} className="recibo-row">
          <span>{it.quantity}x {it.name}</span>
          <span>${Number(it.subtotal ?? (it.price || 0) * (it.quantity || 1)).toFixed(2)}</span>
        </div>
      ))}
      {sale.surcharge > 0 && (
        <div className="recibo-row"><span>Arancel para llevar</span><span>${sale.surcharge.toFixed(2)}</span></div>
      )}
      <div className="recibo-line" />
      <div className="recibo-row recibo-bold">
        <span>TOTAL</span><span>${Number(sale.total || 0).toFixed(2)}</span>
      </div>
      <div>Método de pago: {METHOD_LABELS[sale.payment_method] || sale.payment_method}</div>
      {sale.payment_method === 'cash' && (
        <>
          <div className="recibo-row"><span>Recibido</span><span>${Number(sale.cash_received || 0).toFixed(2)}</span></div>
          <div className="recibo-row"><span>Cambio</span><span>${Number(sale.change_given || 0).toFixed(2)}</span></div>
        </>
      )}
      {sale.payment_method === 'transfer' && (
        <div className="recibo-row"><span>Transferencia</span><span>${Number(sale.transfer_amount || 0).toFixed(2)}</span></div>
      )}
      {sale.payment_method === 'mixed' && (
        <>
          <div className="recibo-row"><span>Efectivo</span><span>${Number(sale.cash_received || 0).toFixed(2)}</span></div>
          <div className="recibo-row"><span>Transferencia</span><span>${Number(sale.transfer_amount || 0).toFixed(2)}</span></div>
          <div className="recibo-row"><span>Cambio</span><span>${Number(sale.change_given || 0).toFixed(2)}</span></div>
        </>
      )}
      <div className="recibo-line" />
      <div className="recibo-center">¡Gracias por su compra!</div>
    </div>
  );
}

export default function ReciboManager({ pendingReceipt, businessName, clientPhone, token }) {
  const [stage, setStage] = useState('idle'); // 'confirm' | 'printing'
  const [widthKey, setWidthKey] = useState(getStoredWidth());

  useEffect(() => {
    if (pendingReceipt) {
      setStage('confirm');
      // ⭐ Se re-lee el ancho cada vez que llega un recibo nuevo (no solo al
      // montar): el ajuste ahora vive en Configuración > Impresión y puede
      // cambiar entre una venta y otra sin recargar la página.
      setWidthKey(getStoredWidth());
    }
  }, [pendingReceipt]);

  useEffect(() => {
    if (stage !== 'printing') return undefined;
    const timer = setTimeout(() => window.print(), 150);
    const handleAfterPrint = () => {
      setStage('idle');
      markReceiptPrinted(pendingReceipt?.id, true, token);
      pendingReceipt?.onDone?.();
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  if (!pendingReceipt) return null;

  const closeWithoutPrinting = () => {
    setStage('idle');
    markReceiptPrinted(pendingReceipt?.id, false, token);
    pendingReceipt.onDone?.();
  };

  return (
    <>
      {stage === 'confirm' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[95] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
            <Printer className="w-10 h-10 text-blue-600 mx-auto mb-3" />
            <h3 className="font-bold text-lg text-gray-900">Pago registrado</h3>
            <p className="text-sm text-gray-600 mt-1">
              Total: ${Number(pendingReceipt.total || 0).toFixed(2)}
              {(pendingReceipt.payment_method === 'cash' || pendingReceipt.payment_method === 'mixed') && pendingReceipt.change_given > 0 && (
                <> · Cambio: ${Number(pendingReceipt.change_given).toFixed(2)}</>
              )}
            </p>
            <p className="text-sm text-gray-700 mt-3 font-semibold">¿Deseas imprimir el recibo?</p>
            <div className="flex gap-3 mt-5">
              <button onClick={closeWithoutPrinting} className="flex-1 py-2 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100 transition">
                No, gracias
              </button>
              <button onClick={() => setStage('printing')} className="flex-1 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition">
                Sí, imprimir
              </button>
            </div>
          </div>
        </div>
      )}
      <ReciboTicket sale={pendingReceipt} businessName={businessName} clientPhone={clientPhone} widthKey={widthKey} />
    </>
  );
}
